import { FormManager } from 'react-form-state-manager';
import { Trans, useTranslation } from 'react-i18next';

import { ErrorBag } from '../../common/helpers';
import UI from '../../common/UI';
import useProject from '../useProject';

interface Ticket {
  id: string;
  title: string;
}

interface TicketForm {
  tickets: { id: string }[];
}

type TicketSelectionFormProps<TTicket extends Ticket, TForm extends TicketForm> = {
  form: FormManager<TForm>;
  tickets: TTicket[];
  errors: ErrorBag;
  event: { id: string };
  isDisabled?: (ticket: TTicket) => boolean;
};

function TicketSelectionForm<TTicket extends Ticket, TForm extends TicketForm>({
  form, tickets, errors, event, isDisabled,
}: TicketSelectionFormProps<TTicket, TForm>) {
  const { t } = useTranslation();
  const project = useProject();

  const allAvailableTickets = typeof isDisabled === 'undefined'
    ? tickets
    : tickets.filter((ticket) => !isDisabled(ticket));

  const selectAllTickets = () => {
    form.set('tickets', allAvailableTickets.map(({ id }) => ({ id })));
  };

  const deselectAllTickets = () => {
    form.set('tickets', []);
  };

  return (
    <UI.InputGroup sc={{ valid: !errors.tickets, touched: form.touched.tickets }}>
      <UI.InputLabel>
        {t('tickets')}
        <UI.Span sc={{ muted: true }}> • </UI.Span>
        <UI.A
          onClick={() => selectAllTickets()}
          sc={{ disabled: form.values.tickets.length === allAvailableTickets.length }}
        >
          {t('select_all')}
        </UI.A>
        <UI.Span sc={{ muted: true }}> • </UI.Span>
        <UI.A
          onClick={() => deselectAllTickets()}
          sc={{ disabled: form.values.tickets.length === 0 }}
        >
          {t('deselect_all')}
        </UI.A>
      </UI.InputLabel>
      {tickets.map((ticket) => (
        <UI.Checkbox
          {...form.checklist('tickets', { id: ticket.id }, { key: 'id' })}
          key={ticket.id}
          disabled={typeof isDisabled === 'undefined' ? false : isDisabled(ticket)}
        >
          {ticket.title}
        </UI.Checkbox>
      ))}
      {tickets.length === 0 && (
        <UI.Div sc={{ muted: true, py: 1 }}>
          <Trans i18nKey="no_tickets_created">
            <UI.Link to={`/${project.id}/events/${event.id}/tickets`} />
          </Trans>
        </UI.Div>
      )}
      <UI.ErrorMessages attribute={t('tickets')} errors={errors.tickets} />
    </UI.InputGroup>
  );
}

export default TicketSelectionForm;
